<template>
  <div class="min-h-screen flex flex-col">
    <img src="@/assets/img/background.png" alt="Background Pattern" class="absolute bottom-[100px] z-[1]" />
    <NavBar v-if="showNavBar" class="z-[9999999]" />
    <div class="flex-1 z-[2]">
      <RouterView />
    </div>
    <!-- Barre horizontale avec dégradé -->
    <CartFooter v-if="showFooterBar" class=""/>
    <div v-if="isKeyboardVisible" class="fixed bottom-0 w-full z-[9999999]">
      <Keyboard @keyPress="onKeyPress"/>
    </div>
    <div class="w-full h-2 fixed bottom-0 z-[9999999]" style="background: linear-gradient(to right, #94C121 0%, #794934 50%, #10458F 100%);"></div>
  </div>
  <ConfirmDialog></ConfirmDialog>
  <Toast class="!z-[9999999]" position="bottom-right"></Toast>
  <Toast class="!z-[9999999]" position="bottom-right" group="inactivity"></Toast>
</template>

<script setup lang="ts">
import {useKeyboard} from "@/composables/useKeyboard.ts";
import NavBar from '@/components/molecule/customer/NavBar.vue'
import CartFooter from '@/components/organism/CartFooter.vue'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const {isKeyboardVisible, onKeyPress} = useKeyboard();

import { useStripeTerminal } from '@/features/stripeTerminal/controller'
const $stripeTerminalStore = useStripeTerminal();

import { useUsers } from "@/features/users/controller";
const $userStore = useUsers();

import { useOrders } from '@/features/orders/controller'
const $ordersStore = useOrders();

import { useToast } from 'primevue/usetoast'
const toast = useToast()

import { useRouter } from 'vue-router';
const router = useRouter();

import { RouterView, useRoute } from 'vue-router'
import type { BigBagOrderInterface, OrderProductInterface } from './features/orders/type';
const route = useRoute()


import { useCart } from '@/services/useCart';

import Keyboard from '@/components/organism/Keyboard.vue';

const warningTimeout = ref<number | null>(null)
const logoutTimeout = ref<number | null>(null)
const automaticLogoutMinutes = ref<number>(2)

const showNavBar = computed(() => {
  return route.name !== 'welcome'
})
const showFooterBar = computed(() => {
  return route.name !== 'welcome' && route.name !== 'login' && route.name !== 'register' && route.name !== 'forgot-password' && route.name !== 'reset-password' && route.name !== 'activate-account' && route.name !== 'mandate-error' && route.name !== 'mandate-success' && route.name !== 'cart' && route.name !== 'order-confirmation'
})

function showWarning() {
  if ($userStore.loggedUser) {
    toast.add({
      severity: 'warn',
      summary: 'Alerte d\'inactivité',
      detail: 'Êtes-vous toujours présent ? Vous allez être déconnecté dans 1 minute.',
      life: 0, // Toast persistant
      group: 'inactivity',
    });
    return
  }
  toast.add({
    severity: 'warn',
    summary: 'Alerte d\'inactivité',
    detail: 'Êtes-vous toujours présent ? Votre panier sera vidé dans 1 minute.',
    life: 0, // Toast persistant
    group: 'inactivity',
  });
}

async function logout() {
  toast.removeAllGroups();
  // If user logged in -> perform full logout
  if ($userStore.loggedUser) {
    toast.add({
      severity: 'warn',
      summary: 'Attention',
      detail: 'Vous avez été déconnecté pour inactivité.',
      life: 4000,
    })
    await $userStore.logout()
    return
  }

  // If anonymous user with items in cart -> clear cart
  if ($ordersStore.activeOrder && (
    ($ordersStore.activeOrder.products && $ordersStore.activeOrder.products.length > 0) ||
    ($ordersStore.activeOrder.big_bags && $ordersStore.activeOrder.big_bags.length > 0)
  )) {
    $ordersStore.activeOrder = null
    toast.add({
      severity: 'info',
      summary: 'Panier vidé',
      detail: 'Votre panier a été vidé pour inactivité.',
      life: 4000,
    })
  }
  if (route.name == 'welcome') return
  toast.add({
    severity: 'warn',
    summary: 'Attention',
    detail: 'Vous avez été redirigé vers la page d\'accueil pour inactivité.',
    life: 4000,
  })
  router.push({ name: 'welcome' });
}

function resetTimers() {
  // Start timers for logged users OR anonymous users who have started a cart
  // const hasActiveCart = $ordersStore.activeOrder && (
  //   ($ordersStore.activeOrder.products && $ordersStore.activeOrder.products.length > 0) ||
  //   ($ordersStore.activeOrder.big_bags && $ordersStore.activeOrder.big_bags.length > 0)
  // );
  // if (!$userStore.loggedUser && !hasActiveCart) return
  if (warningTimeout.value) {
    clearTimeout(warningTimeout.value); warningTimeout.value = null
  }
  if (logoutTimeout.value) {
    clearTimeout(logoutTimeout.value); logoutTimeout.value = null
  }
  toast.removeGroup('inactivity');
  warningTimeout.value = window.setTimeout(showWarning, (automaticLogoutMinutes.value * 60000) / 2)
  logoutTimeout.value = window.setTimeout(logout, automaticLogoutMinutes.value * 60000)
}

function userAction() {
  resetTimers()
}

watch(() => $userStore.loggedUser, async (newVal, oldVal) => {
  if (newVal) {
    resetTimers()
  }
})

onMounted(async () => {
  await $stripeTerminalStore.cancelTerminal();

  let res = await $userStore.getGlobalParams(['automatic_logout_minutes',]);
  if (!res.error && res.data) {
    let setting = res.data.find((param: any) => param.key === 'automatic_logout_minutes')
    automaticLogoutMinutes.value = setting ? parseInt(setting.value) : 2;
  }

  resetTimers()
  window.addEventListener('mousemove', userAction)
  window.addEventListener('keydown', userAction)
  window.addEventListener('click', userAction)
  window.addEventListener('scroll', userAction)
});

onUnmounted(() => {
  if (warningTimeout.value) clearTimeout(warningTimeout.value)
  if (logoutTimeout.value) clearTimeout(logoutTimeout.value)
  window.removeEventListener('mousemove', userAction)
  window.removeEventListener('keydown', userAction)
  window.removeEventListener('click', userAction)
  window.removeEventListener('scroll', userAction)
})
</script>
